import React from 'react';
import DodTermText from './DodTermText';
import './CompliDeskWorkspace.css';
import './PostludePanel.css';

type ConversionProof = {
  kind: string;
  label: string;
  detail: string;
  path?: string;
  at?: string;
};

type TimelinePage = {
  path: string;
  visible_ms: number;
  duration_label?: string;
  seen_at?: string;
  first_seen_at?: string;
  last_seen_at?: string;
};

type PostludeSignal = {
  kind: string;
  path: string;
  note: string;
  seen_at?: string;
};

type PostludeSession = {
  session_id: string;
  user_email: string;
  engaged_label: string;
  converted: boolean;
  plan_name: string;
  started_at?: string;
  ended_at?: string | null;
  open?: boolean;
  open_reason?: string;
  pages: TimelinePage[];
  timeline?: TimelinePage[];
  signals?: PostludeSignal[];
  conversion_proof?: ConversionProof[];
  analysis?: string;
};

type AlertLevel = 'urgent' | 'important' | 'quiet';
type EventAlertLevel = AlertLevel | 'off';
type EventKey = 'arrival' | 'leave' | 'conversion' | 'login_failed' | 'mail_urgent';
type ContactEventLevels = Record<EventKey, EventAlertLevel>;

type PostludeContact = {
  contact_id: string;
  kind: string;
  value: string;
  alert_level?: AlertLevel;
  event_levels?: Partial<ContactEventLevels>;
};

type PostludeSettings = {
  arrival_alerts: boolean;
  leave_analysis: boolean;
  public_alerts: boolean;
  retention_days: number;
  arrival_level?: EventAlertLevel;
  leave_level?: EventAlertLevel;
  conversion_level?: EventAlertLevel;
  login_failed_level?: EventAlertLevel;
  mail_urgent_level?: EventAlertLevel;
};

type TextProvider = {
  name: string;
  configured: boolean;
  region?: string;
};

type DeliveryCategory =
  | 'smtp_config'
  | 'sns_config'
  | 'not_configured'
  | 'destination'
  | 'provider'
  | 'other';

type DeliveryRow = {
  delivery_id: string;
  channel: string;
  destination: string;
  status: string;
  detail: string;
  created_at: string;
  category?: DeliveryCategory | string;
};

type MetricKey =
  | 'visits_today'
  | 'still_open'
  | 'with_account'
  | 'no_account'
  | 'failed_alerts'
  | 'alerts_sent'
  | 'not_configured';

type Observability = {
  open_sessions: number;
  sessions_today: number;
  converted: number;
  unconverted: number;
  deliveries: { sent: number; failed: number; not_configured: number };
  delivery_categories?: Partial<Record<DeliveryCategory, number>>;
  last_delivery_at?: string | null;
  contacts: number;
  text_provider?: TextProvider;
  recent_failures?: DeliveryRow[];
  closed_stale?: number;
  removed_invalid_contacts?: string[];
};

const DELIVERY_CATEGORIES: DeliveryCategory[] = [
  'smtp_config',
  'sns_config',
  'not_configured',
  'destination',
  'provider',
  'other',
];

const VISIT_METRIC_KEYS: MetricKey[] = ['visits_today', 'still_open', 'with_account', 'no_account'];
const DELIVERY_METRIC_KEYS: MetricKey[] = ['failed_alerts', 'alerts_sent', 'not_configured'];

type PostludePanelProps = {
  language?: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string;
};

const EMPTY_SETTINGS: PostludeSettings = {
  arrival_alerts: true,
  leave_analysis: true,
  public_alerts: true,
  retention_days: 90,
};

const EVENT_KEYS: EventKey[] = ['arrival', 'leave', 'conversion', 'login_failed', 'mail_urgent'];
const EVENT_LEVEL_OPTIONS: EventAlertLevel[] = ['off', 'quiet', 'important', 'urgent'];

const defaultEventLevels = (kind: string): ContactEventLevels => (
  kind === 'phone'
    ? {
      arrival: 'off',
      leave: 'off',
      conversion: 'urgent',
      login_failed: 'urgent',
      mail_urgent: 'urgent',
    }
    : {
      arrival: 'quiet',
      leave: 'important',
      conversion: 'urgent',
      login_failed: 'important',
      mail_urgent: 'urgent',
    }
);

const normalizeEventLevels = (
  raw: Partial<ContactEventLevels> | undefined,
  kind: string,
): ContactEventLevels => {
  const base = defaultEventLevels(kind);
  const out = { ...base };
  EVENT_KEYS.forEach((key) => {
    const value = raw?.[key];
    if (value && EVENT_LEVEL_OPTIONS.includes(value)) {
      out[key] = value;
    }
  });
  return out;
};

const levelLabel = (level: string, spanish: boolean): string => {
  const map: Record<string, { en: string; es: string }> = {
    urgent: { en: 'Urgent', es: 'Urgente' },
    important: { en: 'Important', es: 'Importante' },
    quiet: { en: 'Quiet', es: 'Silencioso' },
    off: { en: 'Off', es: 'Apagado' },
  };
  const row = map[level] || map.important;
  return spanish ? row.es : row.en;
};

const eventLabel = (key: EventKey, spanish: boolean): { title: string; hint: string } => {
  const map: Record<EventKey, { en: [string, string]; es: [string, string] }> = {
    arrival: {
      en: ['Someone arrives', 'New visit on the site or console'],
      es: ['Alguien llega', 'Nueva visita al sitio o a la consola'],
    },
    leave: {
      en: ['Note when they leave', 'Summary when the visit ends without a conversion'],
      es: ['Nota al irse', 'Resumen cuando la visita termina sin conversión'],
    },
    conversion: {
      en: ['Conversion / account', 'Signup, successful login, or an active plan'],
      es: ['Conversión / cuenta', 'Registro, inicio de sesión o plan activo'],
    },
    login_failed: {
      en: ['Failed sign-in', 'They tried to sign in and it failed'],
      es: ['Inicio de sesión fallido', 'Intentó entrar y falló'],
    },
    mail_urgent: {
      en: ['Urgent company mail', 'Urgent ticket from the mailbox scan'],
      es: ['Correo urgente de la empresa', 'Ticket urgente del escaneo de buzones'],
    },
  };
  const [title, hint] = spanish ? map[key].es : map[key].en;
  return { title, hint };
};

const formatWhen = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  const stamp = Date.parse(value);
  if (Number.isNaN(stamp)) {
    return value;
  }
  return new Date(stamp).toLocaleString();
};

const PostludePanel: React.FC<PostludePanelProps> = ({
  language = 'en',
  gatewayBaseUrl = '',
  accessToken = '',
}) => {
  const spanish = language === 'es';
  const apiBase = gatewayBaseUrl.replace(/\/$/, '');
  const [sessions, setSessions] = React.useState<PostludeSession[]>([]);
  const [contacts, setContacts] = React.useState<PostludeContact[]>([]);
  const [deliveries, setDeliveries] = React.useState<DeliveryRow[]>([]);
  const [settings, setSettings] = React.useState<PostludeSettings>(EMPTY_SETTINGS);
  const [health, setHealth] = React.useState<Observability | null>(null);
  const [canOperate, setCanOperate] = React.useState(false);
  const [contactKind, setContactKind] = React.useState<'email' | 'phone'>('email');
  const [contactValue, setContactValue] = React.useState('');
  const [contactEventLevels, setContactEventLevels] = React.useState<ContactEventLevels>(() => defaultEventLevels('email'));
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [scanNote, setScanNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [metricFilters, setMetricFilters] = React.useState<MetricKey[]>([]);
  const [deliveryCategoryFilter, setDeliveryCategoryFilter] = React.useState<DeliveryCategory | ''>('');
  const [selectedDeliveryIds, setSelectedDeliveryIds] = React.useState<string[]>([]);
  const [visitsOpen, setVisitsOpen] = React.useState(true);
  const [alertsOpen, setAlertsOpen] = React.useState(false);
  const [sessionsLoadError, setSessionsLoadError] = React.useState('');
  const addDestinationInputRef = React.useRef<HTMLInputElement | null>(null);
  const visitsSectionRef = React.useRef<HTMLDetailsElement | null>(null);
  const alertsSectionRef = React.useRef<HTMLDetailsElement | null>(null);

  const headers = React.useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' } : null
  ), [accessToken]);

  const load = React.useCallback(async () => {
    if (!apiBase || !headers) {
      return;
    }
    const [sessionResponse, contactResponse, settingsResponse, healthResponse, deliveryResponse] = await Promise.all([
      fetch(`${apiBase}/api/v1/postlude/sessions`, { headers }),
      fetch(`${apiBase}/api/v1/postlude/contacts`, { headers }),
      fetch(`${apiBase}/api/v1/postlude/settings`, { headers }),
      fetch(`${apiBase}/api/v1/postlude/observability`, { headers }),
      fetch(`${apiBase}/api/v1/postlude/deliveries?limit=200`, { headers }),
    ]);
    if (sessionResponse.ok) {
      const body = await sessionResponse.json();
      const nextSessions = Array.isArray(body?.sessions) ? body.sessions : [];
      setSessions(nextSessions);
      setSessionsLoadError('');
      if (nextSessions.length) {
        setVisitsOpen(true);
      }
    } else {
      setSessions([]);
      const detail = await sessionResponse.json().catch(() => ({}));
      const rawDetail = detail?.detail;
      const reason = (Array.isArray(rawDetail)
        ? rawDetail.map((item: { msg?: string } | string) => (
          typeof item === 'string' ? item : String(item?.msg || '')
        )).filter(Boolean).join('; ')
        : String(rawDetail || '')).trim();
      setSessionsLoadError(
        spanish
          ? (reason
            ? `No se pudieron cargar las visitas (${sessionResponse.status}): ${reason}`
            : `No se pudieron cargar las visitas (${sessionResponse.status}).`)
          : (reason
            ? `Could not load visits (${sessionResponse.status}): ${reason}`
            : `Could not load visits (${sessionResponse.status}).`),
      );
    }
    if (contactResponse.ok && settingsResponse.ok && healthResponse.ok) {
      const contactBody = await contactResponse.json();
      const settingsBody = await settingsResponse.json();
      const healthBody = await healthResponse.json();
      setContacts(Array.isArray(contactBody?.contacts) ? contactBody.contacts : []);
      setSettings({ ...EMPTY_SETTINGS, ...(settingsBody?.settings || {}) });
      setHealth(healthBody);
      setCanOperate(true);
    } else {
      setCanOperate(false);
    }
    if (deliveryResponse.ok) {
      const deliveryBody = await deliveryResponse.json();
      setDeliveries(Array.isArray(deliveryBody?.deliveries) ? deliveryBody.deliveries : []);
    } else if (healthResponse.ok) {
      const healthBody = await healthResponse.json();
      setDeliveries(Array.isArray(healthBody?.recent_failures) ? healthBody.recent_failures : []);
    }
    setSelectedDeliveryIds([]);
  }, [apiBase, headers, spanish]);

  React.useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  const openAddModal = (kind: 'email' | 'phone') => {
    setContactKind(kind);
    setContactValue('');
    setContactEventLevels(defaultEventLevels(kind));
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setContactValue('');
  };

  React.useEffect(() => {
    if (!addModalOpen) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAddModalOpen(false);
        setContactValue('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const timer = window.setTimeout(() => addDestinationInputRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(timer);
    };
  }, [addModalOpen]);

  const addContact = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiBase || !headers || !contactValue.trim()) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/postlude/contacts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        kind: contactKind,
        value: contactValue.trim(),
        event_levels: contactEventLevels,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setNotice(String(body?.detail || (spanish ? 'No se pudo agregar el destino.' : 'Could not add that destination.')));
      return;
    }
    setContactValue('');
    setContactEventLevels(defaultEventLevels(contactKind));
    setAddModalOpen(false);
    setNotice(spanish ? 'Destino agregado.' : 'Destination added.');
    await load();
  };

  const saveContact = async (
    contact: PostludeContact,
    kind: string,
    value: string,
    eventLevels: ContactEventLevels,
  ) => {
    if (!apiBase || !headers) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/postlude/contacts/${contact.contact_id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ kind, value, event_levels: eventLevels }),
    });
    const body = await response.json().catch(() => ({}));
    setNotice(response.ok
      ? (spanish ? 'Destino actualizado.' : 'Destination updated.')
      : String(body?.detail || (spanish ? 'No se pudo actualizar el destino.' : 'Could not update that destination.')));
    await load();
  };

  const removeContact = async (contactId: string) => {
    if (!apiBase || !headers) {
      return;
    }
    await fetch(`${apiBase}/api/v1/postlude/contacts/${contactId}`, { method: 'DELETE', headers });
    await load();
  };

  const removeSession = async (sessionId: string) => {
    if (!apiBase || !headers) {
      return;
    }
    await fetch(`${apiBase}/api/v1/postlude/sessions/${sessionId}`, { method: 'DELETE', headers });
    if (expandedId === sessionId) {
      setExpandedId(null);
    }
    await load();
  };

  const toggleMetricFilter = (key: MetricKey) => {
    setMetricFilters((current) => {
      let next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
      // Account badges are mutually exclusive.
      if (key === 'with_account' && next.includes('with_account')) {
        next = next.filter((item) => item !== 'no_account');
      }
      if (key === 'no_account' && next.includes('no_account')) {
        next = next.filter((item) => item !== 'with_account');
      }
      const hasVisit = next.some((item) => VISIT_METRIC_KEYS.includes(item));
      const hasDelivery = next.some((item) => DELIVERY_METRIC_KEYS.includes(item));
      if (hasVisit) {
        setVisitsOpen(true);
        window.setTimeout(() => visitsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      }
      if (hasDelivery) {
        setAlertsOpen(true);
        window.setTimeout(() => alertsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      }
      return next;
    });
  };

  const setDeliveryCategory = async (deliveryId: string, category: DeliveryCategory) => {
    if (!apiBase || !headers) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/postlude/deliveries/${deliveryId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ category }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setNotice(String(body?.detail || (spanish ? 'No se pudo cambiar la categoría.' : 'Could not update that category.')));
      return;
    }
    setDeliveries((current) => current.map((row) => (
      row.delivery_id === deliveryId ? { ...row, category } : row
    )));
  };

  const deleteDeliveries = async (ids: string[]) => {
    if (!apiBase || !headers || !ids.length) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/postlude/deliveries/delete`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ delivery_ids: ids }),
    });
    if (!response.ok) {
      setNotice(spanish ? 'No se pudieron borrar esos avisos.' : 'Could not delete those alert records.');
      return;
    }
    setNotice(spanish
      ? `Se borraron ${ids.length} registro(s) de aviso.`
      : `Deleted ${ids.length} alert record(s).`);
    await load();
  };

  const deleteDelivery = async (deliveryId: string) => {
    if (!apiBase || !headers) {
      return;
    }
    await fetch(`${apiBase}/api/v1/postlude/deliveries/${deliveryId}`, { method: 'DELETE', headers });
    setSelectedDeliveryIds((current) => current.filter((id) => id !== deliveryId));
    await load();
  };

  const saveSettings = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiBase || !headers) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/postlude/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings),
    });
    setNotice(response.ok
      ? (spanish ? 'Ajustes guardados.' : 'Settings saved.')
      : (spanish ? 'No se pudieron guardar los ajustes.' : 'Could not save settings.'));
    await load();
  };

  const scan = async () => {
    if (!apiBase || !headers) {
      return;
    }
    setBusy(true);
    setScanNote('');
    try {
      const response = await fetch(`${apiBase}/api/v1/postlude/mail/scan`, { method: 'POST', headers });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setScanNote(spanish ? 'No se pudo leer el correo.' : 'Could not read the mailboxes.');
        return;
      }
      const opened = Array.isArray(body?.tickets) ? body.tickets.length : 0;
      setScanNote(spanish
        ? `Lectura lista. ${opened} tickets nuevos en CompliDesk.`
        : `Scan finished. ${opened} new tickets are on CompliDesk.`);
    } finally {
      setBusy(false);
    }
  };

  const text = health?.text_provider;
  const failed = health?.deliveries.failed ?? 0;
  const sent = health?.deliveries.sent ?? 0;
  const notConfigured = health?.deliveries.not_configured ?? 0;
  const emailContacts = contacts.filter((contact) => contact.kind !== 'phone');
  const phoneContacts = contacts.filter((contact) => contact.kind === 'phone');
  const snsReady = Boolean(text?.configured);
  const todayPrefix = new Date().toISOString().slice(0, 10);
  const activeVisitFilters = metricFilters.filter((key) => VISIT_METRIC_KEYS.includes(key));
  const activeDeliveryFilters = metricFilters.filter((key) => DELIVERY_METRIC_KEYS.includes(key));

  const sessionMatchesFilters = (session: PostludeSession): boolean => {
    if (!activeVisitFilters.length) {
      return true;
    }
    const isOpen = Boolean(session.open ?? !session.ended_at);
    return activeVisitFilters.every((key) => {
      if (key === 'visits_today') {
        return String(session.started_at || '').startsWith(todayPrefix);
      }
      if (key === 'still_open') {
        return isOpen;
      }
      if (key === 'with_account') {
        return Boolean(session.converted);
      }
      if (key === 'no_account') {
        return !session.converted;
      }
      return true;
    });
  };

  const filteredSessions = sessions.filter(sessionMatchesFilters);

  const deliveryStatusForMetric = (key: MetricKey): string | null => {
    if (key === 'failed_alerts') return 'failed';
    if (key === 'alerts_sent') return 'sent';
    if (key === 'not_configured') return 'not_configured';
    return null;
  };

  const filteredDeliveries = deliveries.filter((row) => {
    if (activeDeliveryFilters.length) {
      const statuses = activeDeliveryFilters
        .map(deliveryStatusForMetric)
        .filter((status): status is string => Boolean(status));
      if (!statuses.includes(row.status)) {
        return false;
      }
    } else if (row.status !== 'failed') {
      // Default alert log: failures only until a delivery badge widens it.
      return false;
    }
    if (deliveryCategoryFilter && row.category !== deliveryCategoryFilter) {
      return false;
    }
    return true;
  });

  const allFilteredSelected = filteredDeliveries.length > 0
    && filteredDeliveries.every((row) => selectedDeliveryIds.includes(row.delivery_id));
  const allLoadedSelected = deliveries.length > 0
    && deliveries.every((row) => selectedDeliveryIds.includes(row.delivery_id));
  const alertTone = !health
    ? 'idle'
    : (!snsReady || (failed > 0 && sent === 0))
      ? 'attention'
      : (sent > 0 || snsReady)
        ? 'healthy'
        : 'idle';
  const statusLabel = !health
    ? (spanish ? 'Cargando…' : 'Loading…')
    : !snsReady
      ? (spanish ? 'Textos no listos' : 'Texts not ready')
      : (failed > 0 && sent === 0)
        ? (spanish ? 'Avisos fallando' : 'Alerts failing')
        : (spanish ? 'Amazon SNS listo' : 'Amazon SNS ready');

  const seenTerms = new Set<string>();
  const copy = spanish
    ? {
      eyebrow: 'Visitas y avisos',
      title: 'Postlude',
      lead: 'Postlude anota cada visita al sitio y a la consola. Amazon SNS manda el texto. El correo sale por el correo de la empresa. Los ajustes de abajo deciden cuándo avisar y cuánto tiempo guardar la visita.',
      status: 'Estado',
      visitsToday: 'Visitas hoy',
      stillOpen: 'Abiertas',
      withAccount: 'Con cuenta',
      noAccount: 'Sin cuenta',
      failedAlerts: 'Avisos fallidos',
      alertsSent: 'Avisos enviados',
      notConfigured: 'Sin configurar',
      metricHint: 'Hoy es del día UTC. Con cuenta / Sin cuenta son totales guardados. Los avisos son envíos de correo o texto.',
      settingsTitle: 'Ajustes del espacio',
      settingsLead: 'Estas opciones valen para todo Postlude. El nivel de cada aviso se elige por correo o texto.',
      publicTitle: 'Avisos del sitio público',
      publicHint: 'Incluye visitas al marketing site, no solo la consola.',
      retention: 'Guardar visitas (días)',
      saveSettings: 'Guardar ajustes',
      eventLevelsTitle: 'Avisos para este destino',
      eventLevelsLead: 'Una fila por evento. Off no notifica. Urgente debe tener SMS de respaldo cuando pueda.',
      destinationsTitle: 'A quién llegan los avisos',
      destinationsLead: 'Elija quién recibe cada tipo de aviso y con qué urgencia. El correo usa el buzón de la empresa. El texto usa Amazon SNS.',
      severityLegendTitle: 'Qué significa cada nivel',
      severityOffHint: 'No enviar',
      severityQuietHint: 'Registro / resumen; no interrumpe',
      severityImportantHint: 'Atención en horario laboral',
      severityUrgentHint: 'Actuar ya; combine con SMS',
      smsBackupHint: 'Hay avisos urgentes por correo y ningún texto. Agregue un SMS de respaldo para no depender solo del buzón.',
      presetOps: 'Ops recomendado',
      presetUrgent: 'Solo urgente',
      presetQuiet: 'Solo silencioso',
      presetOff: 'Todo apagado',
      presetsLabel: 'Atajos',
      activeEvents: 'activos',
      mutedDestination: 'Este destino no recibe avisos.',
      adjustLevels: 'Ajustar niveles',
      hideLevels: 'Ocultar niveles',
      alertType: 'Tipo',
      destination: 'Destino',
      add: 'Agregar',
      addEmail: 'Agregar correo',
      addText: 'Agregar texto',
      addDestinationTitle: 'Agregar destino',
      addDestinationLeadEmail: 'Elija la dirección y qué avisos recibe.',
      addDestinationLeadText: 'Elija el número y qué avisos recibe por Amazon SNS.',
      email: 'Correo',
      phone: 'Texto',
      emailChannel: 'Correo',
      textChannel: 'Texto (SMS)',
      save: 'Guardar',
      delete: 'Quitar',
      edit: 'Editar',
      cancel: 'Cancelar',
      close: 'Cerrar',
      noDestinations: 'Nadie recibe avisos todavía. Use + en Correo o Texto para agregar uno.',
      noEmails: 'Ningún correo en la lista.',
      noPhones: 'Ningún teléfono en la lista. El SMS es el respaldo cuando el correo falla o es urgente.',
      needAdmin: 'Inicie sesión como administrador de la plataforma para ver y editar a quién avisar.',
      rosterCount: 'destinos',
      mailTitle: 'Correo de la empresa',
      mailLead: 'Lee los buzones y abre tickets urgentes en CompliDesk.',
      scan: 'Leer correo de la empresa',
      scanning: 'Leyendo…',
      visitsTitle: 'Visitas',
      visitsLead: 'Haga clic en una visita para ver el detalle. Abierta = la pestaña sigue abierta, el aviso de salida no llegó, o aún no pasó el tiempo de inactividad.',
      noVisits: 'Aún no hay visitas registradas.',
      noPages: 'Sin páginas',
      open: 'Abierta',
      subscription: 'Suscripción activa',
      noSubscription: 'Sin suscripción activa',
      deleteVisit: 'Borrar visita',
      type: 'Tipo',
      sessionDetail: 'Detalle de la visita',
      timeline: 'Páginas y tiempo',
      signals: 'Señales',
      conversionProof: 'Prueba de conversión',
      noProof: 'Sin prueba de conversión en esta visita.',
      openWhy: 'Por qué sigue abierta',
      started: 'Inicio',
      ended: 'Fin',
      failuresTitle: 'Registro de avisos',
      failuresLead: 'Categorice y borre registros. Use las insignias de arriba para filtrar por estado.',
      noFailures: 'No hay avisos en este filtro.',
      categoryLabel: 'Categoría',
      categoryAll: 'Todas las categorías',
      deleteSelected: 'Borrar seleccionados',
      deleteOne: 'Borrar',
      selectAllFiltered: 'Seleccionar visibles',
      selectAllLoaded: 'Seleccionar todas',
      clearSelection: 'Quitar selección',
      clearFilters: 'Quitar filtros',
      metricHintFilter: 'Haga clic en una insignia para filtrar visitas o avisos. Vuelva a hacer clic para quitarla.',
      filterShowing: 'Mostrando',
      of: 'de',
      showDetail: 'Ver detalle',
      hideDetail: 'Ocultar detalle',
      catSmtp: 'SMTP / correo',
      catSns: 'SNS / texto',
      catNotConfigured: 'Sin configurar',
      catDestination: 'Destino',
      catProvider: 'Proveedor',
      catOther: 'Otro',
    }
    : {
      eyebrow: 'Visits and alerts',
      title: 'Postlude',
      lead: 'Postlude notes each visit to the public site and the console. Amazon SNS sends the text. Email goes through the company mailbox. The settings below decide when to alert and how long to keep a visit.',
      status: 'Status',
      visitsToday: 'Visits today',
      stillOpen: 'Still open',
      withAccount: 'With an account',
      noAccount: 'No account',
      failedAlerts: 'Failed alerts',
      alertsSent: 'Alerts sent',
      notConfigured: 'Not configured',
      metricHint: 'Today is UTC. With / No account are lifetime stored sessions. Alerts count email or text delivery attempts.',
      settingsTitle: 'Workspace settings',
      settingsLead: 'These options apply to all of Postlude. Per-event levels are set on each email or text.',
      publicTitle: 'Public-site alerts',
      publicHint: 'Include marketing-site visits, not only the console.',
      retention: 'Keep visits (days)',
      saveSettings: 'Save settings',
      eventLevelsTitle: 'Alerts for this destination',
      eventLevelsLead: 'One row per event. Off means do not notify. Pair Urgent with SMS when you can.',
      destinationsTitle: 'Who gets the alerts',
      destinationsLead: 'Choose who hears each event and how loud. Email uses the company mailbox. Text uses Amazon SNS.',
      severityLegendTitle: 'What each level means',
      severityOffHint: 'Do not send',
      severityQuietHint: 'Log / digest; does not interrupt',
      severityImportantHint: 'Act in business hours',
      severityUrgentHint: 'Act now; pair with SMS',
      smsBackupHint: 'Urgent email alerts are on and there is no text destination. Add an SMS backup so you are not mailbox-only.',
      presetOps: 'Ops recommended',
      presetUrgent: 'Urgent only',
      presetQuiet: 'Quiet only',
      presetOff: 'All off',
      presetsLabel: 'Shortcuts',
      activeEvents: 'on',
      mutedDestination: 'This destination will not receive alerts.',
      adjustLevels: 'Adjust levels',
      hideLevels: 'Hide levels',
      alertType: 'Type',
      destination: 'Destination',
      add: 'Add',
      addEmail: 'Add email',
      addText: 'Add text',
      addDestinationTitle: 'Add destination',
      addDestinationLeadEmail: 'Pick the address and which alerts it receives.',
      addDestinationLeadText: 'Pick the number and which alerts it receives via Amazon SNS.',
      email: 'Email',
      phone: 'Text',
      emailChannel: 'Email',
      textChannel: 'Text (SMS)',
      save: 'Save',
      delete: 'Remove',
      edit: 'Edit',
      cancel: 'Cancel',
      close: 'Close',
      noDestinations: 'Nobody gets alerts yet. Use + on Email or Text to add one.',
      noEmails: 'No email destinations yet.',
      noPhones: 'No text destinations yet. SMS is the backup when email fails or the event is urgent.',
      needAdmin: 'Sign in as a platform admin to see and edit who gets alerts.',
      rosterCount: 'destinations',
      mailTitle: 'Company mail',
      mailLead: 'Reads the company mailboxes and opens urgent tickets on CompliDesk.',
      scan: 'Scan company mail',
      scanning: 'Reading…',
      visitsTitle: 'Visits',
      visitsLead: 'Click a visit for the full detail. Open means the tab is still open, the leave beacon never arrived, or the idle cutoff has not hit yet.',
      noVisits: 'No visits recorded yet.',
      noPages: 'No pages',
      open: 'Open',
      subscription: 'Subscription active',
      noSubscription: 'No active subscription',
      deleteVisit: 'Delete visit',
      type: 'Type',
      sessionDetail: 'Visit detail',
      timeline: 'Pages and time',
      signals: 'Signals',
      conversionProof: 'Conversion proof',
      noProof: 'No conversion proof on this visit.',
      openWhy: 'Why it is still open',
      started: 'Started',
      ended: 'Ended',
      failuresTitle: 'Alert log',
      failuresLead: 'Categorize and delete records. Use the badges above to filter by status.',
      noFailures: 'No alerts match this filter.',
      categoryLabel: 'Category',
      categoryAll: 'All categories',
      deleteSelected: 'Delete selected',
      deleteOne: 'Delete',
      selectAllFiltered: 'Select visible',
      selectAllLoaded: 'Select all',
      clearSelection: 'Clear selection',
      clearFilters: 'Clear filters',
      metricHintFilter: 'Click a badge to filter visits or alerts. Click again to turn that filter off.',
      filterShowing: 'Showing',
      of: 'of',
      showDetail: 'Show detail',
      hideDetail: 'Hide detail',
      catSmtp: 'SMTP / email',
      catSns: 'SNS / text',
      catNotConfigured: 'Not configured',
      catDestination: 'Destination',
      catProvider: 'Provider',
      catOther: 'Other',
    };

  const categoryLabel = (category: string): string => {
    const map: Record<string, string> = {
      smtp_config: copy.catSmtp,
      sns_config: copy.catSns,
      not_configured: copy.catNotConfigured,
      destination: copy.catDestination,
      provider: copy.catProvider,
      other: copy.catOther,
    };
    return map[category] || copy.catOther;
  };

  return (
    <section className="postlude" aria-label="Postlude">
      <header className="postlude__hero">
        <div className="postlude__hero-copy">
          <p className="postlude__eyebrow">{copy.eyebrow}</p>
          <h2>{copy.title}</h2>
          <p className="postlude__lead">
            <DodTermText language={spanish ? 'es' : 'en'} seen={seenTerms} text={copy.lead} />
          </p>
        </div>
        <div className={`postlude__status-pill is-${alertTone}`} role="status">
          <span className="postlude__status-dot" aria-hidden="true" />
          <DodTermText language={spanish ? 'es' : 'en'} seen={seenTerms} text={statusLabel} />
        </div>
      </header>

      {health ? (
        <>
          <div className="postlude__metrics" role="toolbar" aria-label={copy.status}>
            {([
              { key: 'visits_today' as MetricKey, value: health.sessions_today, label: copy.visitsToday, tone: '' },
              { key: 'still_open' as MetricKey, value: health.open_sessions, label: copy.stillOpen, tone: health.open_sessions ? 'is-attention' : '' },
              { key: 'with_account' as MetricKey, value: health.converted, label: copy.withAccount, tone: health.converted ? 'is-success' : '' },
              { key: 'no_account' as MetricKey, value: health.unconverted, label: copy.noAccount, tone: '' },
              { key: 'failed_alerts' as MetricKey, value: failed, label: copy.failedAlerts, tone: failed ? 'is-attention' : '' },
              { key: 'alerts_sent' as MetricKey, value: sent, label: copy.alertsSent, tone: sent ? 'is-success' : '' },
              { key: 'not_configured' as MetricKey, value: notConfigured, label: copy.notConfigured, tone: notConfigured ? 'is-attention' : '' },
            ]).filter((item) => item.key !== 'not_configured' || item.value > 0 || metricFilters.includes('not_configured')).map((item) => {
              const pressed = metricFilters.includes(item.key);
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`postlude__metric ${item.tone}${pressed ? ' is-pressed' : ''}`}
                  aria-pressed={pressed}
                  onClick={() => toggleMetricFilter(item.key)}
                >
                  <em>{item.value}</em>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
          <p className="postlude__metric-hint">
            {copy.metricHintFilter}
            {metricFilters.length ? (
              <>
                {' '}
                <button type="button" className="postlude__text-btn" onClick={() => setMetricFilters([])}>
                  {copy.clearFilters}
                </button>
              </>
            ) : null}
          </p>
        </>
      ) : null}

      {notice ? <p className="postlude__notice is-ok" role="status">{notice}</p> : null}
      {scanNote ? <p className="postlude__notice" role="status">{scanNote}</p> : null}

      <div className="postlude__sections">
      <details
        ref={visitsSectionRef}
        className="postlude__details postlude__panel"
        open={visitsOpen}
        onToggle={(event) => setVisitsOpen((event.target as HTMLDetailsElement).open)}
      >
        <summary>
          <h3>{copy.visitsTitle}</h3>
          <span className="postlude__roster-count">
            {activeVisitFilters.length
              ? `${copy.filterShowing} ${filteredSessions.length} ${copy.of} ${sessions.length}`
              : sessions.length}
          </span>
        </summary>
        <div className="postlude__details-body">
        <p className="postlude__details-lead">{copy.visitsLead}</p>
        {filteredSessions.length ? (
          <ul className="postlude__visits">
            {filteredSessions.map((session) => {
              const isOpen = Boolean(session.open ?? !session.ended_at);
              const isExpanded = expandedId === session.session_id;
              const proof = session.conversion_proof || [];
              const timeline = (session.timeline && session.timeline.length)
                ? session.timeline
                : session.pages;
              const signals = session.signals || [];
              return (
                <li key={session.session_id} className={`postlude__visit ${isExpanded ? 'is-expanded' : ''}`}>
                  <button
                    type="button"
                    className="postlude__visit-trigger"
                    aria-expanded={isExpanded}
                    aria-controls={`postlude-detail-${session.session_id}`}
                    onClick={() => setExpandedId(isExpanded ? null : session.session_id)}
                  >
                    <span className="postlude__visit-head">
                      <strong>{session.user_email || (spanish ? 'Sin correo' : 'No email')}</strong>
                      <span className="postlude__badges">
                        {isOpen ? <span className="postlude__badge is-open">{copy.open}</span> : null}
                        {session.converted ? (
                          <span className="postlude__badge is-account">{session.plan_name || copy.subscription}</span>
                        ) : (
                          <span className="postlude__badge">{copy.noSubscription}</span>
                        )}
                      </span>
                    </span>
                    <span className="postlude__visit-summary">
                      <span>{session.engaged_label}</span>
                      <span className="postlude__visit-path">
                        {session.pages.map((page) => page.path).join(' → ') || copy.noPages}
                      </span>
                    </span>
                    <span className="postlude__visit-toggle">{isExpanded ? copy.hideDetail : copy.showDetail}</span>
                  </button>
                  {isExpanded ? (
                    <div
                      className="postlude__visit-detail"
                      id={`postlude-detail-${session.session_id}`}
                      role="region"
                      aria-label={copy.sessionDetail}
                    >
                      <dl className="postlude__meta">
                        <div>
                          <dt>{copy.started}</dt>
                          <dd>{formatWhen(session.started_at) || '—'}</dd>
                        </div>
                        <div>
                          <dt>{copy.ended}</dt>
                          <dd>{isOpen ? copy.open : (formatWhen(session.ended_at) || '—')}</dd>
                        </div>
                      </dl>
                      {isOpen && session.open_reason ? (
                        <div className="postlude__detail-block">
                          <h4>{copy.openWhy}</h4>
                          <p>{session.open_reason}</p>
                        </div>
                      ) : null}
                      {session.analysis ? (
                        <div className="postlude__detail-block">
                          <p>{session.analysis}</p>
                        </div>
                      ) : null}
                      <div className="postlude__detail-block">
                        <h4>{copy.timeline}</h4>
                        {timeline.length ? (
                          <ol className="postlude__timeline">
                            {timeline.map((page, index) => (
                              <li key={`${page.path}-${page.seen_at || page.first_seen_at || index}`}>
                                <code>{page.path}</code>
                                <span>{page.duration_label || `${Math.round((page.visible_ms || 0) / 1000)}s`}</span>
                                {page.seen_at || page.last_seen_at ? (
                                  <time dateTime={page.seen_at || page.last_seen_at}>
                                    {formatWhen(page.seen_at || page.last_seen_at)}
                                  </time>
                                ) : null}
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <p>{copy.noPages}</p>
                        )}
                      </div>
                      <div className="postlude__detail-block">
                        <h4>{copy.conversionProof}</h4>
                        {session.converted && proof.length ? (
                          <ul className="postlude__proof">
                            {proof.map((item, index) => (
                              <li key={`${item.kind}-${item.at || index}`}>
                                <strong>{item.label}</strong>
                                {item.detail ? <span>{item.detail}</span> : null}
                                {item.at ? <time dateTime={item.at}>{formatWhen(item.at)}</time> : null}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>{copy.noProof}</p>
                        )}
                      </div>
                      {signals.length ? (
                        <div className="postlude__detail-block">
                          <h4>{copy.signals}</h4>
                          <ul className="postlude__signals">
                            {signals.map((signal, index) => (
                              <li key={`${signal.kind}-${signal.seen_at || index}`}>
                                <strong>{signal.kind}</strong>
                                <code>{signal.path || '/'}</code>
                                {signal.note ? <span>{signal.note}</span> : null}
                                {signal.seen_at ? <time dateTime={signal.seen_at}>{formatWhen(signal.seen_at)}</time> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {canOperate ? (
                        <div className="postlude__actions">
                          <button type="button" className="dashboard-btn" onClick={() => void removeSession(session.session_id)}>
                            {copy.deleteVisit}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={`postlude__empty${sessionsLoadError ? ' is-error' : ''}`} role={sessionsLoadError ? 'alert' : undefined}>
            {sessionsLoadError
              ? sessionsLoadError
              : sessions.length && activeVisitFilters.length
                ? (spanish ? 'Ninguna visita coincide con este filtro.' : 'No visits match this filter.')
                : copy.noVisits}
          </p>
        )}
        </div>
      </details>
      {canOperate ? (
        <details
          ref={alertsSectionRef}
          className="postlude__details postlude__panel"
          open={alertsOpen}
          onToggle={(event) => setAlertsOpen((event.target as HTMLDetailsElement).open)}
        >
          <summary>
            <h3>{copy.failuresTitle}</h3>
            <span className="postlude__roster-count">
              {copy.filterShowing} {filteredDeliveries.length} {copy.of} {deliveries.length}
            </span>
          </summary>
          <div className="postlude__details-body">
            <p className="postlude__details-lead">{copy.failuresLead}</p>
            <div className="postlude__alert-toolbar">
              <label className="postlude__field postlude__field--inline">
                <span>{copy.categoryLabel}</span>
                <select
                  aria-label={copy.categoryLabel}
                  value={deliveryCategoryFilter}
                  onChange={(event) => setDeliveryCategoryFilter((event.target.value || '') as DeliveryCategory | '')}
                >
                  <option value="">{copy.categoryAll}</option>
                  {DELIVERY_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{categoryLabel(category)}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="dashboard-btn"
                disabled={!filteredDeliveries.length}
                onClick={() => {
                  if (allFilteredSelected) {
                    setSelectedDeliveryIds((current) => current.filter(
                      (id) => !filteredDeliveries.some((row) => row.delivery_id === id),
                    ));
                  } else {
                    setSelectedDeliveryIds((current) => [
                      ...new Set([...current, ...filteredDeliveries.map((row) => row.delivery_id)]),
                    ]);
                  }
                }}
              >
                {allFilteredSelected ? copy.clearSelection : copy.selectAllFiltered}
              </button>
              <button
                type="button"
                className="dashboard-btn"
                disabled={!deliveries.length}
                onClick={() => {
                  if (allLoadedSelected) {
                    setSelectedDeliveryIds([]);
                  } else {
                    setSelectedDeliveryIds(deliveries.map((row) => row.delivery_id));
                  }
                }}
              >
                {allLoadedSelected
                  ? copy.clearSelection
                  : `${copy.selectAllLoaded}${deliveries.length ? ` (${deliveries.length})` : ''}`}
              </button>
              <button
                type="button"
                className="dashboard-btn"
                disabled={!selectedDeliveryIds.length}
                onClick={() => void deleteDeliveries(selectedDeliveryIds)}
              >
                {copy.deleteSelected}
                {selectedDeliveryIds.length ? ` (${selectedDeliveryIds.length})` : ''}
              </button>
            </div>
            {filteredDeliveries.length ? (
              <ul className="postlude__failures">
                {filteredDeliveries.map((row) => {
                  const checked = selectedDeliveryIds.includes(row.delivery_id);
                  const category = (row.category || 'other') as DeliveryCategory;
                  return (
                    <li key={row.delivery_id} className={`postlude__failure is-${row.status}`}>
                      <label className="postlude__failure-select">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSelectedDeliveryIds((current) => (
                              checked
                                ? current.filter((id) => id !== row.delivery_id)
                                : [...current, row.delivery_id]
                            ));
                          }}
                          aria-label={`${row.channel} ${row.destination}`}
                        />
                      </label>
                      <div className="postlude__failure-body">
                        <div className="postlude__failure-head">
                          <strong>{row.channel}</strong>
                          <span className={`postlude__status-chip is-${row.status}`}>{row.status}</span>
                          <span className="postlude__failure-dest">{row.destination}</span>
                        </div>
                        <span className="postlude__failure-detail">{row.detail || row.status}</span>
                        <div className="postlude__failure-meta">
                          <label className="postlude__field postlude__field--inline">
                            <span>{copy.categoryLabel}</span>
                            <select
                              aria-label={copy.categoryLabel}
                              value={DELIVERY_CATEGORIES.includes(category) ? category : 'other'}
                              onChange={(event) => void setDeliveryCategory(
                                row.delivery_id,
                                event.target.value as DeliveryCategory,
                              )}
                            >
                              {DELIVERY_CATEGORIES.map((item) => (
                                <option key={item} value={item}>{categoryLabel(item)}</option>
                              ))}
                            </select>
                          </label>
                          <time dateTime={row.created_at}>{formatWhen(row.created_at)}</time>
                          <button
                            type="button"
                            className="dashboard-btn"
                            onClick={() => void deleteDelivery(row.delivery_id)}
                          >
                            {copy.deleteOne}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="postlude__empty">{copy.noFailures}</p>
            )}
          </div>
        </details>
      ) : null}
      <details className="postlude__details postlude__panel postlude__panel--destinations">
        <summary>
          <h3>{copy.destinationsTitle}</h3>
          {canOperate ? (
            <span className="postlude__roster-count">
              {contacts.length} {copy.rosterCount}
            </span>
          ) : null}
        </summary>
        <div className="postlude__details-body">
          <p className="postlude__details-lead">
            <DodTermText language={spanish ? 'es' : 'en'} seen={seenTerms} text={copy.destinationsLead} />
          </p>
          {!canOperate ? (
            <p className="postlude__empty">{copy.needAdmin}</p>
          ) : (
            <>
              <div className="postlude__severity-legend" aria-label={copy.severityLegendTitle}>
                <p className="postlude__severity-legend-title">{copy.severityLegendTitle}</p>
                <ul>
                  <li className="is-off"><strong>{levelLabel('off', spanish)}</strong><span>{copy.severityOffHint}</span></li>
                  <li className="is-quiet"><strong>{levelLabel('quiet', spanish)}</strong><span>{copy.severityQuietHint}</span></li>
                  <li className="is-important"><strong>{levelLabel('important', spanish)}</strong><span>{copy.severityImportantHint}</span></li>
                  <li className="is-urgent"><strong>{levelLabel('urgent', spanish)}</strong><span>{copy.severityUrgentHint}</span></li>
                </ul>
              </div>
              {emailContacts.some((contact) => hasUrgentEvents(normalizeEventLevels(contact.event_levels, contact.kind)))
                && !phoneContacts.length ? (
                  <p className="postlude__guardrail" role="status">
                    {copy.smsBackupHint}
                    {' '}
                    <button type="button" className="postlude__text-btn" onClick={() => openAddModal('phone')}>
                      {copy.addText}
                    </button>
                  </p>
                ) : null}
              <div className="postlude__roster">
                <div className="postlude__roster-col is-email">
                  <header className="postlude__roster-head">
                    <div className="postlude__roster-head-copy">
                      <span className="postlude__channel-badge is-email">{copy.emailChannel}</span>
                      <span className="postlude__roster-sub">{copy.email}</span>
                    </div>
                    <div className="postlude__roster-head-actions">
                      <em aria-label={`${emailContacts.length} ${copy.emailChannel}`}>{emailContacts.length}</em>
                      <button
                        type="button"
                        className="postlude__add-btn"
                        aria-label={copy.addEmail}
                        onClick={() => openAddModal('email')}
                      >
                        <span aria-hidden="true">+</span>
                      </button>
                    </div>
                  </header>
                  {emailContacts.length ? (
                    <ul className="postlude__roster-list">
                      {emailContacts.map((contact) => (
                        <ContactCard
                          key={contact.contact_id}
                          contact={contact}
                          copy={copy}
                          spanish={spanish}
                          smsBackupReady={phoneContacts.length > 0}
                          onSave={saveContact}
                          onDelete={removeContact}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="postlude__empty postlude__empty--soft">{copy.noEmails}</p>
                  )}
                </div>
                <div className="postlude__roster-col is-text">
                  <header className="postlude__roster-head">
                    <div className="postlude__roster-head-copy">
                      <span className="postlude__channel-badge is-text">{copy.textChannel}</span>
                      <span className="postlude__roster-sub">
                        <DodTermText language={spanish ? 'es' : 'en'} seen={seenTerms} text="Amazon SNS" />
                      </span>
                    </div>
                    <div className="postlude__roster-head-actions">
                      <em aria-label={`${phoneContacts.length} ${copy.textChannel}`}>{phoneContacts.length}</em>
                      <button
                        type="button"
                        className="postlude__add-btn is-text"
                        aria-label={copy.addText}
                        onClick={() => openAddModal('phone')}
                      >
                        <span aria-hidden="true">+</span>
                      </button>
                    </div>
                  </header>
                  {phoneContacts.length ? (
                    <ul className="postlude__roster-list">
                      {phoneContacts.map((contact) => (
                        <ContactCard
                          key={contact.contact_id}
                          contact={contact}
                          copy={copy}
                          spanish={spanish}
                          smsBackupReady
                          onSave={saveContact}
                          onDelete={removeContact}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="postlude__empty postlude__empty--soft">{copy.noPhones}</p>
                  )}
                </div>
              </div>
              {!contacts.length ? <p className="postlude__empty">{copy.noDestinations}</p> : null}
            </>
          )}
        </div>
      </details>

      {canOperate ? (
        <details className="postlude__details postlude__panel postlude__panel--levels">
          <summary>
            <h3>{copy.settingsTitle}</h3>
          </summary>
          <form className="postlude__details-body" onSubmit={(event) => void saveSettings(event)}>
            <p className="postlude__details-lead">{copy.settingsLead}</p>
            <div className="postlude__settings-footer">
              <label className="postlude__toggle">
                <input
                  type="checkbox"
                  checked={settings.public_alerts}
                  onChange={(event) => setSettings((current) => ({ ...current, public_alerts: event.target.checked }))}
                />
                <span className="postlude__toggle-copy">
                  <strong>{copy.publicTitle}</strong>
                  <span>{copy.publicHint}</span>
                </span>
              </label>
              <label className="postlude__retention">
                <span>{copy.retention}</span>
                <input
                  type="number"
                  min={7}
                  max={365}
                  value={settings.retention_days}
                  onChange={(event) => setSettings((current) => ({ ...current, retention_days: Number(event.target.value) }))}
                />
              </label>
              <div className="postlude__actions">
                <button type="submit" className="dashboard-btn dashboard-btn-primary">{copy.saveSettings}</button>
              </div>
            </div>
          </form>
        </details>
      ) : null}


      {canOperate ? (
        <details className="postlude__details postlude__panel">
          <summary>
            <h3>{copy.mailTitle}</h3>
          </summary>
          <div className="postlude__details-body">
            <p className="postlude__details-lead">{copy.mailLead}</p>
            <div className="postlude__actions">
              <button
                type="button"
                className="dashboard-btn dashboard-btn-primary"
                disabled={busy || !accessToken}
                onClick={() => void scan()}
              >
                {busy ? copy.scanning : copy.scan}
              </button>
            </div>
          </div>
        </details>
      ) : null}

      </div>

      {addModalOpen && canOperate ? (
        <div
          className="postlude__modal-backdrop"
          role="presentation"
          onClick={closeAddModal}
        >
          <div
            className="postlude__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="postlude-add-destination-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="postlude__modal-head">
              <div>
                <p className="postlude__eyebrow">
                  {contactKind === 'phone' ? copy.textChannel : copy.emailChannel}
                </p>
                <h3 id="postlude-add-destination-title">{copy.addDestinationTitle}</h3>
                <p>
                  {contactKind === 'phone' ? copy.addDestinationLeadText : copy.addDestinationLeadEmail}
                </p>
              </div>
              <button
                type="button"
                className="postlude__modal-close"
                aria-label={copy.close}
                onClick={closeAddModal}
              >
                ×
              </button>
            </header>
            <form className="postlude__modal-form" onSubmit={(event) => void addContact(event)}>
              <label className="postlude__field">
                <span>{copy.destination}</span>
                <input
                  ref={addDestinationInputRef}
                  aria-label={copy.destination}
                  value={contactValue}
                  placeholder={contactKind === 'phone' ? '+12105551212' : 'operations@nextcompli.com'}
                  onChange={(event) => setContactValue(event.target.value)}
                  autoComplete={contactKind === 'phone' ? 'tel' : 'email'}
                  inputMode={contactKind === 'phone' ? 'tel' : 'email'}
                />
              </label>
              <EventLevelMatrix
                levels={contactEventLevels}
                spanish={spanish}
                kind={contactKind}
                title={copy.eventLevelsTitle}
                lead={copy.eventLevelsLead}
                presetsLabel={copy.presetsLabel}
                presetOps={copy.presetOps}
                presetUrgent={copy.presetUrgent}
                presetQuiet={copy.presetQuiet}
                presetOff={copy.presetOff}
                onChange={setContactEventLevels}
              />
              <div className="postlude__modal-actions">
                <button type="button" className="dashboard-btn" onClick={closeAddModal}>
                  {copy.cancel}
                </button>
                <button type="submit" className="dashboard-btn dashboard-btn-primary" disabled={!contactValue.trim()}>
                  {copy.add}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const eventChipLabel = (key: EventKey, spanish: boolean): string => {
  const map: Record<EventKey, { en: string; es: string }> = {
    arrival: { en: 'Arrive', es: 'Llega' },
    leave: { en: 'Leave', es: 'Sale' },
    conversion: { en: 'Convert', es: 'Convierte' },
    login_failed: { en: 'Sign-in', es: 'Login' },
    mail_urgent: { en: 'Mail', es: 'Correo' },
  };
  return spanish ? map[key].es : map[key].en;
};

const LEVEL_RANK: Record<EventAlertLevel, number> = {
  off: 0,
  quiet: 1,
  important: 2,
  urgent: 3,
};

const highestEventLevel = (levels: ContactEventLevels): EventAlertLevel => (
  EVENT_KEYS.reduce<EventAlertLevel>((top, key) => (
    LEVEL_RANK[levels[key]] > LEVEL_RANK[top] ? levels[key] : top
  ), 'off')
);

const hasUrgentEvents = (levels: ContactEventLevels): boolean => (
  EVENT_KEYS.some((key) => levels[key] === 'urgent')
);

type LevelPresetId = 'ops' | 'urgent' | 'quiet' | 'off';

const levelPreset = (id: LevelPresetId, kind: string): ContactEventLevels => {
  if (id === 'off') {
    return {
      arrival: 'off',
      leave: 'off',
      conversion: 'off',
      login_failed: 'off',
      mail_urgent: 'off',
    };
  }
  if (id === 'urgent') {
    return {
      arrival: 'off',
      leave: 'off',
      conversion: 'urgent',
      login_failed: 'urgent',
      mail_urgent: 'urgent',
    };
  }
  if (id === 'quiet') {
    return {
      arrival: 'quiet',
      leave: 'quiet',
      conversion: 'quiet',
      login_failed: 'quiet',
      mail_urgent: 'quiet',
    };
  }
  return defaultEventLevels(kind);
};

type DestinationCopy = {
  type: string;
  destination: string;
  email: string;
  phone: string;
  save: string;
  delete: string;
  edit: string;
  cancel: string;
  eventLevelsTitle: string;
  eventLevelsLead: string;
  presetsLabel: string;
  presetOps: string;
  presetUrgent: string;
  presetQuiet: string;
  presetOff: string;
  activeEvents: string;
  mutedDestination: string;
  adjustLevels: string;
  hideLevels: string;
};

const EventLevelMatrix: React.FC<{
  levels: ContactEventLevels;
  spanish: boolean;
  kind: string;
  title: string;
  lead: string;
  presetsLabel: string;
  presetOps: string;
  presetUrgent: string;
  presetQuiet: string;
  presetOff: string;
  onChange: (levels: ContactEventLevels) => void;
}> = ({
  levels,
  spanish,
  kind,
  title,
  lead,
  presetsLabel,
  presetOps,
  presetUrgent,
  presetQuiet,
  presetOff,
  onChange,
}) => (
  <div className="postlude__event-matrix" aria-label={title}>
    <div className="postlude__event-matrix-head">
      <strong>{title}</strong>
      <span>{lead}</span>
    </div>
    <div className="postlude__presets" role="group" aria-label={presetsLabel}>
      <span className="postlude__presets-label">{presetsLabel}</span>
      {([
        ['ops', presetOps],
        ['urgent', presetUrgent],
        ['quiet', presetQuiet],
        ['off', presetOff],
      ] as Array<[LevelPresetId, string]>).map(([id, label]) => (
        <button
          key={id}
          type="button"
          className="postlude__preset-btn"
          onClick={() => onChange(levelPreset(id, kind))}
        >
          {label}
        </button>
      ))}
    </div>
    <ul className="postlude__event-matrix-list">
      {EVENT_KEYS.map((key) => {
        const labels = eventLabel(key, spanish);
        return (
          <li key={key} className={`is-level-${levels[key]}`}>
            <div className="postlude__event-matrix-copy">
              <strong>{labels.title}</strong>
              <span>{labels.hint}</span>
            </div>
            <div className="postlude__segment" role="radiogroup" aria-label={labels.title}>
              {EVENT_LEVEL_OPTIONS.map((level) => (
                <button
                  key={level}
                  type="button"
                  role="radio"
                  className={`postlude__segment-btn is-${level}${levels[key] === level ? ' is-active' : ''}`}
                  aria-checked={levels[key] === level}
                  onClick={() => onChange({ ...levels, [key]: level })}
                >
                  {levelLabel(level, spanish)}
                </button>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

const ContactCard: React.FC<{
  contact: PostludeContact;
  copy: DestinationCopy;
  spanish?: boolean;
  smsBackupReady?: boolean;
  onSave: (contact: PostludeContact, kind: string, value: string, eventLevels: ContactEventLevels) => Promise<void>;
  onDelete: (contactId: string) => Promise<void>;
}> = ({ contact, copy, spanish = false, smsBackupReady = false, onSave, onDelete }) => {
  const [editing, setEditing] = React.useState(false);
  const [levelsOpen, setLevelsOpen] = React.useState(false);
  const [kind, setKind] = React.useState(contact.kind);
  const [value, setValue] = React.useState(contact.value);
  const [eventLevels, setEventLevels] = React.useState<ContactEventLevels>(() => (
    normalizeEventLevels(contact.event_levels, contact.kind)
  ));
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setKind(contact.kind);
    setValue(contact.value);
    setEventLevels(normalizeEventLevels(contact.event_levels, contact.kind));
    setEditing(false);
  }, [contact.kind, contact.value, contact.event_levels, contact.contact_id]);

  const activeCount = EVENT_KEYS.filter((key) => eventLevels[key] !== 'off').length;
  const peak = highestEventLevel(eventLevels);
  const urgentWithoutSms = contact.kind !== 'phone' && hasUrgentEvents(eventLevels) && !smsBackupReady;

  const persistLevels = async (next: ContactEventLevels) => {
    setEventLevels(next);
    setSaving(true);
    try {
      await onSave(contact, contact.kind, contact.value, next);
    } finally {
      setSaving(false);
    }
  };

  const matrixProps = {
    levels: eventLevels,
    spanish,
    kind: contact.kind,
    title: copy.eventLevelsTitle,
    lead: copy.eventLevelsLead,
    presetsLabel: copy.presetsLabel,
    presetOps: copy.presetOps,
    presetUrgent: copy.presetUrgent,
    presetQuiet: copy.presetQuiet,
    presetOff: copy.presetOff,
  };

  if (!editing) {
    return (
      <li className={[
        'postlude__contact-card',
        `is-peak-${peak}`,
        activeCount === 0 ? 'is-muted' : '',
        urgentWithoutSms ? 'needs-sms' : '',
      ].filter(Boolean).join(' ')}>
        <div className="postlude__contact-card-main">
          <div className="postlude__contact-identity">
            <strong className="postlude__contact-value">{contact.value}</strong>
            <span className={`postlude__channel-badge ${contact.kind === 'phone' ? 'is-text' : 'is-email'}`}>
              {contact.kind === 'phone' ? copy.phone : copy.email}
            </span>
          </div>
          <div className="postlude__contact-status">
            <span className={`postlude__level-badge is-${peak}`}>
              {activeCount === 0 ? levelLabel('off', spanish) : levelLabel(peak, spanish)}
            </span>
            <span className="postlude__contact-active">
              {activeCount}/{EVENT_KEYS.length} {copy.activeEvents}
            </span>
          </div>
        </div>
        {activeCount === 0 ? (
          <p className="postlude__contact-muted">{copy.mutedDestination}</p>
        ) : (
          <ul className="postlude__level-rail" aria-label={copy.eventLevelsTitle}>
            {EVENT_KEYS.map((key) => {
              const level = eventLevels[key];
              return (
                <li key={key} className={`postlude__level-rail-item is-${level}`}>
                  <span className="postlude__level-rail-dot" aria-hidden="true" />
                  <span className="postlude__level-rail-label">{eventChipLabel(key, spanish)}</span>
                  <em>{levelLabel(level, spanish)}</em>
                </li>
              );
            })}
          </ul>
        )}
        <details
          className="postlude__contact-levels"
          open={levelsOpen}
          onToggle={(event) => setLevelsOpen((event.target as HTMLDetailsElement).open)}
        >
          <summary>
            {levelsOpen ? copy.hideLevels : copy.adjustLevels}
            {saving ? <span className="postlude__saving">{spanish ? 'Guardando…' : 'Saving…'}</span> : null}
          </summary>
          <EventLevelMatrix
            {...matrixProps}
            onChange={(next) => void persistLevels(next)}
          />
        </details>
        <div className="postlude__contact-card-actions">
          <button type="button" className="dashboard-btn" onClick={() => setEditing(true)}>
            {copy.edit}
          </button>
          <button type="button" className="dashboard-btn" onClick={() => void onDelete(contact.contact_id)}>
            {copy.delete}
          </button>
        </div>
      </li>
    );
  }
  return (
    <li className="postlude__contact-card is-editing">
      <form
        className="postlude__contact-edit"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave(contact, kind, value, eventLevels).then(() => setEditing(false));
        }}
      >
        <div className="postlude__contact-row">
          <label className="postlude__field">
            <span>{copy.type}</span>
            <select
              aria-label={copy.type}
              value={kind}
              onChange={(event) => {
                const nextKind = event.target.value;
                setKind(nextKind);
                setEventLevels((current) => normalizeEventLevels(current, nextKind));
              }}
            >
              <option value="email">{copy.email}</option>
              <option value="phone">{copy.phone}</option>
            </select>
          </label>
          <label className="postlude__field">
            <span>{copy.destination}</span>
            <input aria-label={copy.destination} value={value} onChange={(event) => setValue(event.target.value)} />
          </label>
        </div>
        <EventLevelMatrix
          {...matrixProps}
          levels={eventLevels}
          kind={kind}
          onChange={setEventLevels}
        />
        <div className="postlude__contact-card-actions">
          <button type="submit" className="dashboard-btn dashboard-btn-primary">{copy.save}</button>
          <button
            type="button"
            className="dashboard-btn"
            onClick={() => {
              setKind(contact.kind);
              setValue(contact.value);
              setEventLevels(normalizeEventLevels(contact.event_levels, contact.kind));
              setEditing(false);
            }}
          >
            {copy.cancel}
          </button>
        </div>
      </form>
    </li>
  );
};

export default PostludePanel;
